import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileCheck2,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
  XCircle,
} from 'lucide-react';

import {
  getOnboardingAuditDashboard,
  getOnboardingCandidateAudit,
} from '@/api/contractorOnboardingAdminApi';

import {
  Badge,
} from '@/components/ui/badge';

import {
  Button,
} from '@/components/ui/button';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Input,
} from '@/components/ui/input';


const statusLabel = {
  incomplete:
    'Incomplete',

  review_required:
    'Review required',

  replacement_required:
    'Replacement required',

  ready_for_approval:
    'Ready for approval',

  approved:
    'Approved',
};


const statusClass = {
  incomplete:
    'border-slate-500/30 bg-slate-500/10 text-slate-300',

  review_required:
    'border-amber-500/30 bg-amber-500/10 text-amber-400',

  replacement_required:
    'border-red-500/30 bg-red-500/10 text-red-400',

  ready_for_approval:
    'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',

  approved:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
};


const formatDate = (
  value
) => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(
      value
    ).toLocaleString();
  } catch {
    return String(
      value
    );
  }
};


const shortAgent = (
  value
) => {
  if (!value) {
    return 'Not captured';
  }

  const text = String(
    value
  );

  let os = 'Unknown device';
  let browser = 'Unknown browser';

  if (
    /Windows NT 10/i.test(
      text
    )
  ) {
    os = 'Windows';
  } else if (
    /Android/i.test(
      text
    )
  ) {
    os = 'Android';
  } else if (
    /iPhone|iPad/i.test(
      text
    )
  ) {
    os = 'iOS';
  } else if (
    /Mac OS X/i.test(
      text
    )
  ) {
    os = 'macOS';
  }

  if (
    /Edg\//i.test(
      text
    )
  ) {
    browser = 'Edge';
  } else if (
    /Chrome\//i.test(
      text
    )
  ) {
    browser = 'Chrome';
  } else if (
    /Firefox\//i.test(
      text
    )
  ) {
    browser = 'Firefox';
  } else if (
    /Safari\//i.test(
      text
    )
  ) {
    browser = 'Safari';
  }

  return `${os} / ${browser}`;
};


const metricCards = (
  summary
) => [
  {
    key:
      'total',

    label:
      'Total Candidates',

    value:
      summary?.total || 0,

    icon:
      Users,
  },

  {
    key:
      'review_required',

    label:
      'Awaiting Review',

    value:
      summary?.review_required || 0,

    icon:
      Clock3,
  },

  {
    key:
      'replacement_required',

    label:
      'Replacement Required',

    value:
      summary?.replacement_required || 0,

    icon:
      AlertTriangle,
  },

  {
    key:
      'ready_for_approval',

    label:
      'Ready for Approval',

    value:
      summary?.ready_for_approval || 0,

    icon:
      FileCheck2,
  },

  {
    key:
      'approved',

    label:
      'Approved',

    value:
      summary?.approved || 0,

    icon:
      ShieldCheck,
  },

  {
    key:
      'admitted',

    label:
      'Admitted',

    value:
      summary?.admitted || 0,

    icon:
      UserCheck,
  },
];


const TimelineIcon = ({
  type,
}) => {
  if (
    String(
      type
    ).includes(
      'approved'
    )
  ) {
    return (
      <ShieldCheck className="h-4 w-4 text-emerald-400" />
    );
  }

  if (
    String(
      type
    ).includes(
      'rejected'
    )
  ) {
    return (
      <XCircle className="h-4 w-4 text-red-400" />
    );
  }

  return (
    <CheckCircle2 className="h-4 w-4 text-cyan-400" />
  );
};


export default function ContractorOnboardingAuditDashboard() {

  const [
    dashboard,
    setDashboard,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState('');

  const [
    search,
    setSearch,
  ] = useState('');

  const [
    statusFilter,
    setStatusFilter,
  ] = useState('all');

  const [
    selectedCandidate,
    setSelectedCandidate,
  ] = useState(null);

  const [
    audit,
    setAudit,
  ] = useState(null);

  const [
    auditLoading,
    setAuditLoading,
  ] = useState(false);


  const loadDashboard = useCallback(
    async () => {
      setLoading(true);
      setError('');

      try {
        const result =
          await getOnboardingAuditDashboard();

        setDashboard(
          result
        );

      } catch (err) {
        setError(
          err?.response?.data?.detail
          || err?.message
          || 'Unable to load onboarding audit dashboard'
        );

      } finally {
        setLoading(false);
      }
    },
    []
  );


  useEffect(
    () => {
      loadDashboard();
    },
    [
      loadDashboard,
    ]
  );


  const openAudit = async (
    candidate
  ) => {
    if (
      !candidate?.invitation_id
    ) {
      return;
    }

    setSelectedCandidate(
      candidate
    );

    setAudit(null);
    setAuditLoading(true);
    setError('');

    try {
      const result =
        await getOnboardingCandidateAudit(
          candidate.invitation_id
        );

      setAudit(
        result
      );

    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to load candidate audit trail'
      );

    } finally {
      setAuditLoading(false);
    }
  };


  const candidates = useMemo(
    () => {
      const rows =
        dashboard?.candidates
        || [];

      const q =
        search
        .trim()
        .toLowerCase();

      return rows.filter(
        (row) => {
          if (
            statusFilter !== 'all'
            &&
            row.workflow_status
            !== statusFilter
          ) {
            return false;
          }

          if (!q) {
            return true;
          }

          return [
            row.candidate_name,
            row.candidate_email,
            row.contractor_name,
            row.workflow_status,
          ]
            .some(
              (value) =>
                String(
                  value
                  || ''
                )
                  .toLowerCase()
                  .includes(
                    q
                  )
            );
        }
      );
    },
    [
      dashboard,
      search,
      statusFilter,
    ]
  );


  if (
    loading
    && !dashboard
  ) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }


  return (
    <div className="space-y-6">

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Contractor Onboarding Audit
          </h2>

          <p className="mt-1 text-sm text-muted-foreground">
            One-stop lifecycle, provenance, evidence review and approval audit.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={
            loadDashboard
          }
          disabled={
            loading
          }
        >
          <RefreshCw
            className={
              `mr-2 h-4 w-4 ${
                loading
                  ? 'animate-spin'
                  : ''
              }`
            }
          />

          Refresh
        </Button>

      </div>


      {
        error
          ? (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
              {error}
            </div>
          )
          : null
      }


      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        {
          metricCards(
            dashboard?.summary
          ).map(
            ({
              key,
              label,
              value,
              icon: Icon,
            }) => (
              <Card key={key}>
                <CardContent className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {label}
                    </p>

                    <p className="mt-2 text-3xl font-bold">
                      {value}
                    </p>
                  </div>

                  <Icon className="h-6 w-6 text-muted-foreground" />
                </CardContent>
              </Card>
            )
          )
        }
      </div>


      <Card>
        <CardHeader>
          <CardTitle>
            Candidate Register
          </CardTitle>
        </CardHeader>

        <CardContent>

          <div className="mb-5 flex flex-col gap-3 lg:flex-row">

            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

              <Input
                value={search}
                onChange={
                  (event) =>
                    setSearch(
                      event.target.value
                    )
                }
                placeholder="Search candidate, email or contractor"
                className="pl-9"
              />
            </div>

            <select
              value={statusFilter}
              onChange={
                (event) =>
                  setStatusFilter(
                    event.target.value
                  )
              }
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="all">
                All statuses
              </option>

              <option value="incomplete">
                Incomplete
              </option>

              <option value="review_required">
                Review required
              </option>

              <option value="replacement_required">
                Replacement required
              </option>

              <option value="ready_for_approval">
                Ready for approval
              </option>

              <option value="approved">
                Approved
              </option>
            </select>

          </div>


          <div className="overflow-x-auto rounded-lg border">

            <table className="w-full text-sm">

              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">
                    Candidate
                  </th>

                  <th className="px-4 py-3 text-left">
                    Contractor
                  </th>

                  <th className="px-4 py-3 text-left">
                    Submitted
                  </th>

                  <th className="px-4 py-3 text-left">
                    Evidence
                  </th>

                  <th className="px-4 py-3 text-left">
                    Status
                  </th>

                  <th className="px-4 py-3 text-left">
                    Submission IP
                  </th>

                  <th className="px-4 py-3 text-right">
                    Audit
                  </th>
                </tr>
              </thead>


              <tbody>
                {
                  candidates.length
                    ? (
                      candidates.map(
                        (row) => (
                          <tr
                            key={
                              row.invitation_id
                            }
                            className="border-t"
                          >

                            <td className="px-4 py-4">
                              <button
                                type="button"
                                onClick={
                                  () =>
                                    openAudit(
                                      row
                                    )
                                }
                                className="text-left font-semibold text-primary hover:underline"
                              >
                                {
                                  row.candidate_name
                                  || 'Unnamed candidate'
                                }
                              </button>

                              <div className="mt-1 text-xs text-muted-foreground">
                                {
                                  row.candidate_email
                                  || '—'
                                }
                              </div>
                            </td>


                            <td className="px-4 py-4">
                              {
                                row.contractor_name
                                || '—'
                              }
                            </td>


                            <td className="px-4 py-4 whitespace-nowrap">
                              {
                                formatDate(
                                  row.submitted_at
                                )
                              }
                            </td>


                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="font-medium">
                                {
                                  row.evidence_accepted_count
                                  || 0
                                }
                              </span>

                              <span className="text-muted-foreground">
                                /
                                {
                                  row.evidence_current_count
                                  || 0
                                }
                              </span>
                            </td>


                            <td className="px-4 py-4">
                              <Badge
                                variant="outline"
                                className={
                                  statusClass[
                                    row.workflow_status
                                  ]
                                  || ''
                                }
                              >
                                {
                                  statusLabel[
                                    row.workflow_status
                                  ]
                                  || row.workflow_status
                                  || 'Unknown'
                                }
                              </Badge>
                            </td>


                            <td className="px-4 py-4">
                              {
                                row.submission_ip
                                  ? (
                                    <div>
                                      <div className="font-medium">
                                        {row.submission_ip}
                                      </div>

                                      <div className="mt-1 text-xs text-muted-foreground">
                                        {
                                          shortAgent(
                                            row.submission_user_agent
                                          )
                                        }
                                      </div>
                                    </div>
                                  )
                                  : (
                                    <span className="text-xs text-muted-foreground">
                                      Not historically captured
                                    </span>
                                  )
                              }
                            </td>


                            <td className="px-4 py-4 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={
                                  () =>
                                    openAudit(
                                      row
                                    )
                                }
                              >
                                View
                                <ChevronRight className="ml-1 h-4 w-4" />
                              </Button>
                            </td>

                          </tr>
                        )
                      )
                    )
                    : (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-10 text-center text-muted-foreground"
                        >
                          No candidates match the current filters.
                        </td>
                      </tr>
                    )
                }
              </tbody>

            </table>

          </div>

        </CardContent>
      </Card>


      <Dialog
        open={
          Boolean(
            selectedCandidate
          )
        }
        onOpenChange={
          (open) => {
            if (!open) {
              setSelectedCandidate(
                null
              );

              setAudit(
                null
              );
            }
          }
        }
      >

        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">

          <DialogHeader>
            <DialogTitle>
              Candidate Audit — {
                selectedCandidate?.candidate_name
                || 'Candidate'
              }
            </DialogTitle>

            <DialogDescription>
              {
                selectedCandidate?.contractor_name
                || ''
              }
              {
                selectedCandidate?.candidate_email
                  ? ` • ${selectedCandidate.candidate_email}`
                  : ''
              }
            </DialogDescription>
          </DialogHeader>


          {
            auditLoading
              ? (
                <div className="flex min-h-[260px] items-center justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )
              : audit
                ? (
                  <div className="space-y-6">

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">

                      <Card>
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">
                            Acknowledgement IP
                          </p>

                          <p className="mt-2 font-semibold">
                            {
                              audit.provenance
                                ?.acknowledgement_source_ip
                              || 'Not captured'
                            }
                          </p>

                          <p className="mt-1 text-xs text-muted-foreground">
                            {
                              shortAgent(
                                audit.provenance
                                  ?.acknowledgement_user_agent
                              )
                            }
                          </p>
                        </CardContent>
                      </Card>


                      <Card>
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">
                            Submission IP
                          </p>

                          <p className="mt-2 font-semibold">
                            {
                              audit.provenance
                                ?.submission_source_ip
                              || 'Not historically captured'
                            }
                          </p>

                          <p className="mt-1 text-xs text-muted-foreground">
                            {
                              audit.provenance
                                ?.submission_source_ip
                                ? shortAgent(
                                    audit.provenance
                                      ?.submission_user_agent
                                  )
                                : 'No value inferred'
                            }
                          </p>
                        </CardContent>
                      </Card>


                      <Card>
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">
                            Final Approval
                          </p>

                          <p className="mt-2 font-semibold">
                            {
                              audit.approval
                                ?.status
                              || 'Not approved'
                            }
                          </p>

                          <p className="mt-1 text-xs text-muted-foreground">
                            {
                              audit.approval
                                ?.approved_by
                                ? `By ${audit.approval.approved_by}`
                                : '—'
                            }
                          </p>
                        </CardContent>
                      </Card>


                      <Card>
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">
                            Approval Time
                          </p>

                          <p className="mt-2 font-semibold">
                            {
                              formatDate(
                                audit.approval
                                  ?.approved_at
                              )
                            }
                          </p>
                        </CardContent>
                      </Card>

                    </div>


                    {
                      audit.approval
                        ?.snapshot_sha256
                        ? (
                          <div className="rounded-lg border bg-muted/20 p-4">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Immutable approval snapshot SHA-256
                            </p>

                            <p className="mt-2 break-all font-mono text-xs">
                              {
                                audit.approval
                                  .snapshot_sha256
                              }
                            </p>
                          </div>
                        )
                        : null
                    }


                    <div>

                      <h3 className="text-lg font-semibold">
                        Chronological Audit Trail
                      </h3>

                      <p className="mt-1 text-sm text-muted-foreground">
                        Candidate, document, review, replacement and approval events.
                      </p>


                      <div className="mt-5 space-y-0">

                        {
                          audit.timeline?.map(
                            (
                              event,
                              index
                            ) => (
                              <div
                                key={
                                  `${event.at}-${event.event_type}-${index}`
                                }
                                className="relative flex gap-4 pb-6"
                              >

                                {
                                  index
                                  < (
                                    audit.timeline.length
                                    - 1
                                  )
                                    ? (
                                      <div className="absolute left-[9px] top-6 h-full w-px bg-border" />
                                    )
                                    : null
                                }


                                <div className="relative z-10 mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background">
                                  <TimelineIcon
                                    type={
                                      event.event_type
                                    }
                                  />
                                </div>


                                <div className="min-w-0 flex-1 rounded-lg border p-4">

                                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">

                                    <div>
                                      <p className="font-semibold">
                                        {
                                          event.title
                                          || event.event_type
                                        }
                                      </p>

                                      {
                                        event.actor
                                          ? (
                                            <p className="mt-1 text-xs text-muted-foreground">
                                              Actor: {
                                                event.actor
                                              }
                                            </p>
                                          )
                                          : null
                                      }
                                    </div>

                                    <p className="whitespace-nowrap text-xs text-muted-foreground">
                                      {
                                        formatDate(
                                          event.at
                                        )
                                      }
                                    </p>

                                  </div>


                                  {
                                    event.provenance
                                      ?.source_ip
                                      ? (
                                        <div className="mt-3 rounded-md bg-muted/30 p-3 text-xs">
                                          <div>
                                            IP:
                                            {' '}
                                            <span className="font-medium">
                                              {
                                                event.provenance
                                                  .source_ip
                                              }
                                            </span>
                                          </div>

                                          {
                                            event.provenance
                                              ?.user_agent
                                              ? (
                                                <div className="mt-1 text-muted-foreground">
                                                  {
                                                    shortAgent(
                                                      event.provenance
                                                        .user_agent
                                                    )
                                                  }
                                                </div>
                                              )
                                              : null
                                          }
                                        </div>
                                      )
                                      : (
                                        event.event_type
                                        === 'application_submitted'
                                          ? (
                                            <div className="mt-3 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                                              Submission IP was not historically captured for this application.
                                            </div>
                                          )
                                          : null
                                      )
                                  }


                                  {
                                    event.detail
                                      ? (
                                        <details className="mt-3">
                                          <summary className="cursor-pointer text-xs text-muted-foreground">
                                            Event details
                                          </summary>

                                          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-md bg-muted/30 p-3 text-xs">
                                            {
                                              typeof event.detail
                                              === 'string'
                                                ? event.detail
                                                : JSON.stringify(
                                                    event.detail,
                                                    null,
                                                    2
                                                  )
                                            }
                                          </pre>
                                        </details>
                                      )
                                      : null
                                  }

                                </div>

                              </div>
                            )
                          )
                        }

                      </div>

                    </div>

                  </div>
                )
                : null
          }

        </DialogContent>
      </Dialog>

    </div>
  );
}
